import { useCallback, useEffect, useState } from "react";
import { FileText, GraduationCap, Megaphone, Plus, Trash2, Files } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function InvCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
      <Icon className="w-5 h-5 text-[#D4AF37] mb-3" />
      <div className="font-heading text-2xl text-white">{value}</div>
      <div className="text-xs text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminContent() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = useCallback(() => api.get("/admin/content").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/announcements", form); toast.success("Announcement published"); setForm({ title: "", body: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/announcements/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const inv = d?.inventory || {};
  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <InvCard icon={Files} label="Resources" value={inv.resources ?? "—"} />
        <InvCard icon={GraduationCap} label="Courses" value={inv.courses ?? "—"} />
        <InvCard icon={FileText} label="Documents" value={inv.documents ?? "—"} />
        <InvCard icon={FileText} label="Contracts" value={inv.contracts ?? "—"} />
        <InvCard icon={Files} label="Client docs" value={inv.client_documents ?? "—"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#D4AF37]" /> Publish announcement</h3>
          <form onSubmit={create} className="space-y-3" data-testid="admin-announcement-form">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Title" data-testid="admin-ann-title" className="bg-[#0d0d0f] border-white/10 text-white" />
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required placeholder="Message to your users…" data-testid="admin-ann-body" className="bg-[#0d0d0f] border-white/10 text-white min-h-[90px]" />
            <Button type="submit" data-testid="admin-ann-publish" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" /> Publish</Button>
          </form>
          <div className="mt-4 space-y-2" data-testid="admin-announcements">
            {(d?.announcements || []).map((a) => (
              <div key={a.announcement_id} className="flex items-start justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-3">
                <div className="min-w-0"><div className="text-white text-sm">{a.title}</div><div className="text-xs text-[#71717A] line-clamp-1">{a.body}</div></div>
                <button onClick={() => remove(a.announcement_id)} data-testid={`admin-ann-delete-${a.announcement_id}`} className="text-[#71717A] hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(d?.announcements || []).length === 0 && <p className="text-sm text-[#71717A]">No announcements yet.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-4">Learning catalog</h3>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {(d?.courses || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-3">
                <div><div className="text-white text-sm">{c.title}</div><div className="text-xs text-[#71717A]">{c.category} · {c.lessons} lessons</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
